import { useEffect } from 'react';
import { useSharedValue, useAnimatedStyle, withRepeat, withTiming, cancelAnimation, Easing, withSequence } from 'react-native-reanimated';
import { useReadyPcsV2Values } from './useReadyPcsV2Values';
import { ReadyPcsV2Props } from '@components/common/ReadyPcsV2';
import { DEFAULT_BULLET_ANIMATION_DURATION } from '@components/common/ReadyPcsV2/constants';

interface UseManageReadyPcsV2BulletsParams extends Pick<ReadyPcsV2Props, 'activeBulletConfig' | 'quantityProduced' | 'quantityTarget'> {}

export const useManageReadyPcsV2BulletAnimation = ({
  quantityProduced,
  quantityTarget,
  activeBulletConfig,
}: UseManageReadyPcsV2BulletsParams) => {
  const {
    amountOfBullets,
    shouldRenderBullets,
    getBulletBg,
    progressFlex,
    remainingFlex,
    timelineBackgroundColor,
    finishedBulletColor,
    isCalculationComplete,
    hasOverlap,
    overlappedBulletColor,
    producedQuantity,
    targetQuantity,
  } = useReadyPcsV2Values({ quantityProduced, quantityTarget });

  const animationConfig = activeBulletConfig?.animationConfig;
  const isAnimationActive = animationConfig?.isActive ?? false;
  const duration = animationConfig?.duration ?? DEFAULT_BULLET_ANIMATION_DURATION;
  const startOpacity = animationConfig?.startOpacity ?? 0;

  const opacity = useSharedValue(1);

  // Half duration for each direction (1 -> startOpacity -> 1)
  const halfDuration = duration / 2;

  useEffect(() => {
    if (isAnimationActive) {
      // Calculate the current phase based on global time to synchronize with other animations
      const currentPhase = (Date.now() % duration) / duration;

      // Determine if we're in first half (1->min) or second half (min->1)
      const isFirstHalf = currentPhase < 0.5;
      const phaseInHalf = isFirstHalf ? currentPhase * 2 : (currentPhase - 0.5) * 2;

      // Calculate current opacity based on phase (1 -> startOpacity -> 1)
      const currentOpacity = isFirstHalf ? 1 - phaseInHalf * (1 - startOpacity) : startOpacity + phaseInHalf * (1 - startOpacity);

      opacity.value = currentOpacity;

      // Calculate remaining time in current half
      const remainingInHalf = halfDuration * (1 - phaseInHalf);

      if (isFirstHalf) {
        // Complete first half (1 -> min), then start full pulsing cycle
        opacity.value = withTiming(startOpacity, { duration: remainingInHalf, easing: Easing.out(Easing.ease) }, () => {
          opacity.value = withRepeat(
            withSequence(
              withTiming(1, { duration: halfDuration, easing: Easing.out(Easing.ease) }),
              withTiming(startOpacity, { duration: halfDuration, easing: Easing.out(Easing.ease) })
            ),
            -1,
            false
          );
        });
      } else {
        // Complete second half (min -> 1), then start full pulsing cycle
        opacity.value = withTiming(1, { duration: remainingInHalf, easing: Easing.out(Easing.ease) }, () => {
          opacity.value = withRepeat(
            withSequence(
              withTiming(startOpacity, { duration: halfDuration, easing: Easing.out(Easing.ease) }),
              withTiming(1, { duration: halfDuration, easing: Easing.out(Easing.ease) })
            ),
            -1,
            false
          );
        });
      }
    } else {
      cancelAnimation(opacity);
      opacity.value = 1;
    }

    return () => cancelAnimation(opacity);
  }, [isAnimationActive, duration, startOpacity]);

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
  }));

  // Only render animated bullet when there's a target to progress toward
  const shouldRenderAnimatedBullet = activeBulletConfig && isAnimationActive && isCalculationComplete;

  // Use overlap color when at or over target (e.g., 2/2 or 3/2) in bullets mode, otherwise use the config color
  const isAtOrOverTarget = typeof producedQuantity === 'number' && typeof targetQuantity === 'number' && producedQuantity >= targetQuantity;
  const animationColor = shouldRenderBullets && isAtOrOverTarget ? overlappedBulletColor : animationConfig?.color;

  // Determine which bullet index should be animated:
  // - When hasOverlap: animate an extra bullet at the end (return null)
  // - When producedQuantity < totalPcs: animate the bullet at index producedQuantity (the next one to fill)
  const getAnimatedBulletIndex = (): number | null => {
    if (!shouldRenderAnimatedBullet) return null;
    if (hasOverlap) return null;
    if (typeof producedQuantity === 'number' && typeof targetQuantity === 'number' && producedQuantity < targetQuantity) {
      return producedQuantity;
    }
    return null;
  };

  const animatedBulletIndex = getAnimatedBulletIndex();

  return {
    amountOfBullets,
    shouldRenderBullets,
    getBulletBg,
    progressFlex,
    remainingFlex,
    timelineBackgroundColor,
    finishedBulletColor,
    animatedStyle,
    shouldRenderAnimatedBullet,
    activeBulletStyle: activeBulletConfig?.containerStyle,
    animationColor,
    animatedBulletIndex,
  };
};
